// src/components/GlobalToolbar.tsx
/**
 * T-34: Global Toolbar Component
 * 
 * Tool palette providing:
 * - Pan, Jump, Measurement, Annotation tools
 * - Keyboard shortcut indicators
 * - Active tool visual feedback
 * - Responsive collapse behavior
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../types/redux';
import { useDialog, useSidePanel } from './ui';
import { ShareDialogContent } from './ShareDialogContent';
import { LoadDialogContent } from './LoadDialogContent';
import { PreferencesContent } from './PreferencesContent';
import { useAnnotationToolTracking } from '../hooks/useAnnotationToolTracking';
import { setViewMode } from '../store/slices/equivalentFractionsSlice';
import { ToolMode, TOOL_METADATA } from '../store/slices/toolModeSlice';

// Tool configuration with icons and shortcuts
const TOOLS = [
  {
    id: 'pan' as ToolMode,
    ...TOOL_METADATA.pan,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M7 11.5l5-5 5 5M7 19.5l5-5 5 5" />
      </svg>
    ),
  },
  {
    id: 'jump' as ToolMode,
    ...TOOL_METADATA.jump,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M13 7l5 5-5 5M6 12h12" />
      </svg>
    ),
  },
  {
    id: 'measurement' as ToolMode,
    ...TOOL_METADATA.measurement,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M7 21l10-10M7 7l10 10" />
      </svg>
    ),
  },
  {
    id: 'annotation' as ToolMode,
    ...TOOL_METADATA.annotation,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
      </svg>
    ),
  },
  {
    id: 'expression' as ToolMode,
    ...TOOL_METADATA.expression,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: 'equation' as ToolMode,
    ...TOOL_METADATA.equation,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    id: 'point' as ToolMode,
    ...TOOL_METADATA.point,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    ),
  },
  {
    id: 'shape' as ToolMode,
    ...TOOL_METADATA.shape,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    ),
  },
  {
    id: 'transform' as ToolMode,
    ...TOOL_METADATA.transform,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
  {
    id: 'vector' as ToolMode,
    ...TOOL_METADATA.vector,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M13 7l5 5m0 0l-5 5m5-5H6" />
      </svg>
    ),
  }
];

interface GlobalToolbarProps {
  collapsed: boolean;
}

export function GlobalToolbar({ collapsed }: GlobalToolbarProps) {
  const dispatch = useDispatch();
  const preferences = useSelector((state: RootState) => state.preferences);
  const viewMode = useSelector((state: RootState) => state.equivalentFractions.mode);
  
  // New UI framework hooks
  const { openDialog } = useDialog();
  const { openSidePanel } = useSidePanel();
  
  // Use local state for tool management until Redux slice is added
  const [localActiveTool, setLocalActiveTool] = React.useState<ToolMode>('pan');
  
  // Check if toolMode slice exists in Redux store
  const storeHasToolMode = useSelector((state: RootState) => {
    try {
      return 'toolMode' in state && state.toolMode !== undefined;
    } catch {
      return false;
    }
  });
  
  // Try to get Redux tool state safely
  const reduxActiveTool = useSelector((state: RootState) => {
    try {
      return storeHasToolMode ? (state as any).toolMode?.activeTool : null;
    } catch {
      return null;
    }
  });

  // Use Redux state if available, otherwise use local state
  const activeTool = storeHasToolMode && reduxActiveTool ? reduxActiveTool : localActiveTool;
  
  // Use annotation tool tracking hook
  const { switchToolMode } = useAnnotationToolTracking();

  // Keyboard shortcut handling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts when no input is focused
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      const key = e.key.toLowerCase();
      const tool = TOOLS.find(t => t.shortcut.toLowerCase() === key);
      
      if (tool) {
        e.preventDefault();
        handleToolSelect(tool.id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []); // No dependencies needed

  const handleToolSelect = (toolId: ToolMode) => {
    console.log('Tool selected:', toolId);
    
    if (storeHasToolMode) {
      // Use the annotation tool tracking hook
      switchToolMode(toolId);
      console.log('Switched tool mode with annotation tracking:', toolId);
    } else {
      // Use local state fallback
      setLocalActiveTool(toolId);
      console.log('Using local state fallback:', toolId);
    }
  };

  // Dialog trigger functions using new UI framework
  const handleShare = () => {
    openDialog(<ShareDialogContent />, "Share Number Line", { size: 'md' });
  };

  const handleLoad = () => {
    openDialog(<LoadDialogContent />, "Load Shared Scene", { size: 'md' });
  };

  const handlePreferences = () => {
    openSidePanel(<PreferencesContent />, "Preferences");
  };

  const viewModeOptions = [
    { id: 'standard', label: 'Standard line', description: 'Single line or double number line view' },
    { id: 'equivalentFractions', label: 'Equivalent fractions', description: 'Stacked aligned lines' },
    { id: 'coordinateGrid', label: 'Coordinate grid', description: '2D axes with grid' },
  ];
  
  const visibleViewModeOptions = viewModeOptions;

  const handleViewModeChange = (mode: 'standard' | 'equivalentFractions' | 'coordinateGrid') => {
    dispatch(setViewMode({ mode }));
  };

  // Filter tools based on view mode - coordinate grid tools only show in coordinate grid mode
  const visibleTools = TOOLS.filter(tool => {
    if (tool.id === 'equation' || tool.id === 'point' || tool.id === 'shape' || tool.id === 'transform' || tool.id === 'vector') {
      return viewMode === 'coordinateGrid';
    }
    return true;
  });

  if (collapsed) {
    // Collapsed view - icon-only buttons
    return (
      <div className="p-2 space-y-1">
        {visibleTools.map((tool) => (
          <button
            key={tool.id}
            onClick={() => handleToolSelect(tool.id)}
            className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-200 ${
              activeTool === tool.id
                ? preferences.theme === 'dark'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-blue-500 text-white shadow-lg'
                : preferences.theme === 'dark'
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
            title={`${tool.name} (${tool.shortcut})`}
            aria-label={tool.name}
          >
            {tool.icon}
          </button>
        ))}
      </div>
    );
  }

  // Expanded view - full toolbar with labels
  return (
    <div className="p-4">


      {/* Toolbar Header */}
      <div className="mb-4">
        <h2 className={`text-sm font-semibold mb-2 ${
          preferences.theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
        }`}>
          Tools
        </h2>
        <p className={`text-xs ${
          preferences.theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>
          Use keyboard shortcuts or click to select
        </p>
      </div>

      {/* View Mode */}
      <div className="mb-4">
        <h2 className={`text-sm font-semibold mb-2 ${
          preferences.theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
        }`}>
          View Mode
        </h2>
        <div className="grid grid-cols-1 gap-1.5">
          {visibleViewModeOptions.map((mode) => (
            <button
              key={mode.id}
              onClick={() => handleViewModeChange(mode.id as 'standard' | 'equivalentFractions' | 'coordinateGrid')}
              className={`w-full px-3 py-2 rounded-lg border text-left transition-all duration-200 ${
                viewMode === mode.id
                  ? preferences.theme === 'dark'
                    ? 'border-blue-500 bg-blue-900/20 text-white'
                    : 'border-blue-500 bg-blue-50 text-gray-900'
                  : preferences.theme === 'dark'
                    ? 'border-gray-700 text-gray-200 hover:border-gray-600 hover:bg-gray-800'
                    : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="font-medium">{mode.label}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{mode.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Tool Buttons */}
      <div className="space-y-2">
        {visibleTools.map((tool) => (
          <button
            key={tool.id}
            onClick={() => handleToolSelect(tool.id)}
            className={`w-full p-3 rounded-lg transition-all duration-200 text-left group ${
              activeTool === tool.id
                ? preferences.theme === 'dark'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-blue-500 text-white shadow-lg'
                : preferences.theme === 'dark'
                  ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-3">
              {/* Icon */}
              <div className="flex-shrink-0">
                {tool.icon}
              </div>
              
              {/* Tool Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">
                    {tool.name}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${
                    activeTool === tool.id
                      ? 'bg-blue-400 bg-opacity-30'
                      : preferences.theme === 'dark'
                        ? 'bg-gray-600 bg-opacity-50'
                        : 'bg-gray-200'
                  }`}>
                    {tool.shortcut}
                  </span>
                </div>
                <p className={`text-xs mt-1 ${
                  activeTool === tool.id
                    ? 'text-blue-100'
                    : preferences.theme === 'dark'
                      ? 'text-gray-400'
                      : 'text-gray-500'
                }`}>
                  {tool.description}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>


    </div>
  );
}